import { TOKEN_STORAGE_KEY, WORKSPACE_STORAGE_KEY } from '@/config/env';

/**
 * The access token and the active workspace, outside React.
 *
 * Deliberately framework-free: the axios interceptor needs both synchronously
 * on every request, and importing a React context there would be a cycle back
 * through the component tree. `subscribe` lets React read them through
 * `useSyncExternalStore` without the store knowing React exists.
 *
 * **On localStorage.** It is readable by any script that gets injected into the
 * page, so an httpOnly cookie is strictly safer. The reason it is not one yet:
 * connecting a social account is a full-window redirect to the provider and
 * back (`SocialAccountsPage` sets `window.location.href`), so a token held only
 * in memory would drop the session on every connect, and on every reload. A
 * cookie is the right follow-up — it needs CORS credentials, SameSite handling
 * across the OAuth round-trip and CSRF tokens, which is a change of its own.
 */
function createStore(key: string) {
  let value: string | null = read();
  const listeners = new Set<() => void>();

  function read(): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      // Private browsing can throw on access.
      return null;
    }
  }

  function emit() {
    for (const listener of listeners) listener();
  }

  return {
    get: () => value,
    set(next: string) {
      value = next;
      try {
        localStorage.setItem(key, next);
      } catch {
        /* still applies for this session */
      }
      emit();
    },
    clear() {
      value = null;
      try {
        localStorage.removeItem(key);
      } catch {
        /* nothing to do */
      }
      emit();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const tokenStore = createStore(TOKEN_STORAGE_KEY);
export const workspaceStore = createStore(WORKSPACE_STORAGE_KEY);
